import { EmailRounded, ExpandMoreRounded } from "@mui/icons-material";
import { Box, Button, Collapse, Paper, Typography } from "@mui/material";
import { useRef, useState } from "react";
import AcxEmailChipInput, {
    AcxEmailChipInputHandle,
    EmailSuggestion,
} from "components/UI/AcxEmailChipInput";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import {
    KboFlowRunOutput,
    KboReportEmail,
} from "../KboFlowDebug/KboFlowRunDebug";
import { formatDuration, formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import { DigestCard, Stat } from "./KboFlowRunDetailPrimitives";
import { QuestionStepCalls } from "./KboFlowRunLlmCalls";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import {
    DigestQuestion,
    asNumber,
    asRecord,
    asString,
    getContext,
    getDigestQuestions,
    getExplorationForQuestion,
    getPhase,
    getQuestionAnalysis,
    getQuestionReport,
    questionStatus,
} from "./KboFlowRunDigestModels";
import { AnalysisNarrative } from "./KboFlowRunAnalysisNarrative";
import { ExplorationNarrative } from "./KboFlowRunExplorationNarrative";
import { StepReviewPanel } from "./KboFlowRunFeedback";

/** Per-question tab: the multi-level analysis and the report it produced. */

/**
 * The report exactly as it was generated. `htmlBody` is the fully-rendered email
 * document, so it goes in a fully-sandboxed iframe (no scripts, no same-origin
 * access) rather than being injected into the page.
 */
function ReportPreview({ email }: { email: KboReportEmail }) {
    const [expanded, setExpanded] = useState(false);
    const html = email.htmlBody;
    const text = email.plainTextBody;
    if (!html && !text) return null;

    return (
        <Paper
            variant="outlined"
            sx={{ borderRadius: 1.5, mt: 1.5, overflow: "hidden" }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    bgcolor: "#F9FAFB",
                    borderBottom: expanded ? "1px solid" : 0,
                    borderColor: "divider",
                    display: "flex",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: pageColors.muted,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        What landed in the inbox
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 750 }}>
                        {email.subject ?? "No subject line"}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: pageColors.muted }}
                    >
                        From {email.fromDisplayName ?? "KBo"}
                        {email.fromAddress ? ` · ${email.fromAddress}` : ""}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    onClick={() => setExpanded((value) => !value)}
                    startIcon={
                        <ExpandMoreRounded
                            sx={{
                                transform: expanded
                                    ? "rotate(180deg)"
                                    : "none",
                                transition: "transform 120ms",
                            }}
                        />
                    }
                >
                    {expanded ? "Hide" : "Show"}
                </Button>
            </Box>
            <Collapse in={expanded} timeout={180}>
                {html ? (
                    <Box
                        component="iframe"
                        title="Generated report"
                        srcDoc={html}
                        sandbox=""
                        sx={{
                            bgcolor: pageColors.white,
                            border: 0,
                            display: "block",
                            height: 560,
                            width: "100%",
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            bgcolor: pageColors.white,
                            fontFamily: "monospace",
                            fontSize: 13,
                            maxHeight: 560,
                            overflowY: "auto",
                            p: 2,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {text}
                    </Box>
                )}
            </Collapse>
        </Paper>
    );
}

export function QuestionView({
    output,
    question,
    questionIndex,
    emailSuggestions,
    emailSuggestionsLoading,
    emailSubmitting,
    onSendReportEmail,
}: {
    output: KboFlowRunOutput;
    question: DigestQuestion;
    questionIndex: number;
    emailSuggestions: EmailSuggestion[];
    emailSuggestionsLoading: boolean;
    emailSubmitting: boolean;
    onSendReportEmail: (
        recipients: string[],
        reportIndex: number,
    ) => Promise<boolean>;
}) {
    const analysis = getQuestionAnalysis(output, question);
    // v5 runs record the analysis as an exploration tree; prefer it over the linear
    // multi-level narrative when present for this question.
    const exploration = getExplorationForQuestion(output, question.id);
    const report = getQuestionReport(output, question);
    // The backend addresses reports by their index in output.reports; getQuestionReport
    // returns an element of that array, so indexOf gives the exact report to email.
    const reportIndex = report ? (output.reports ?? []).indexOf(report) : -1;
    const pursued = !!analysis || !!exploration;

    const [recipients, setRecipients] = useState<string[]>([]);
    const [pendingText, setPendingText] = useState("");
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const emailInputRef = useRef<AcxEmailChipInputHandle>(null);

    const handleSendEmail = async () => {
        // Commit any address still typed in the box so it is included, not dropped.
        const finalRecipients =
            emailInputRef.current?.flushPending() ?? recipients;
        const sent = await onSendReportEmail(finalRecipients, reportIndex);
        // Keep the recipient list on success so the report can be re-sent without
        // re-entering addresses.
        if (sent) setEmailModalOpen(false);
    };
    // Feedback rows key off the persisted KboFlowQuestion id, so panels are only
    // offered for questions the run output actually carries an id for.
    const feedbackQuestionId = question.id;
    const status = questionStatus(output, question);
    const context = getContext(output);
    const userName = asString(context.userProfile?.name) ?? "the recipient";
    const analysisPhase = getPhase(output, "MultiLevelAnalysis", "Analysis");
    const baseline = asRecord(context.signalSnapshot?.baseline);
    const totalConversations = asNumber(baseline?.totalConversations);
    const questionsTotal = getDigestQuestions(output).length;
    const maxPursue = output.request?.parameters?.maxQuestionsToPursue ?? 1;
    const totalRunCalls = (output.phaseRecords ?? []).reduce(
        (total, phase) => total + (phase.invocations?.length ?? 0),
        0,
    );
    const runFailedCalls = (output.phaseRecords ?? [])
        .flatMap((phase) => phase.invocations ?? [])
        .filter(
            (call) => call.error || call.status?.toLowerCase() === "failed",
        ).length;
    const runClosedAt = output.completedAtUtc
        ? new Intl.DateTimeFormat(undefined, {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
          }).format(new Date(output.completedAtUtc))
        : "—";

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper
                variant="outlined"
                sx={{
                    bgcolor: pursued ? pageColors.blue : "#F7F8FA",
                    borderColor: pursued ? pageColors.blue : "divider",
                    borderRadius: 2,
                    color: pursued ? pageColors.white : pageColors.ink,
                    p: { xs: 2.5, md: 3.5 },
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        color: pursued ? pageColors.white : pageColors.ink,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        opacity: pursued ? 0.75 : 0.65,
                    }}
                >
                    Question {questionIndex + 1} of {questionsTotal} ·{" "}
                    {status === "held"
                        ? "accepted, not pursued"
                        : status === "delivered"
                        ? "pursued and delivered"
                        : status}
                </Typography>
                <Typography
                    component="h2"
                    sx={{
                        color: pursued ? pageColors.white : pageColors.ink,
                        fontSize: { xs: 23, md: 28 },
                        fontWeight: 750,
                        lineHeight: 1.25,
                        mt: 0.25,
                    }}
                >
                    {question.text}
                </Typography>
                <Typography
                    sx={{
                        color: pursued ? pageColors.white : pageColors.ink,
                        mt: 0.75,
                        opacity: pursued ? 0.78 : 0.65,
                    }}
                >
                    {pursued
                        ? `How KBo narrowed this question down to one finding, and what ${userName} received. See the Whole run tab for how it was written and judged.`
                        : "Cleared the judges. Stopped before analysis because this run had reached its configured pursuit limit."}
                </Typography>
            </Paper>

            {pursued ? (
                <>
                    <DigestCard
                        id="phase-4"
                        number={1}
                        color={pageColors.blue}
                        title="KBo digs in, narrowing as it goes"
                        meta={
                            <Typography variant="body2" color="text.secondary">
                                {formatDuration(
                                    analysisPhase?.startedAtUtc,
                                    analysisPhase?.completedAtUtc,
                                )}
                                {" · "}
                                {analysisPhase?.invocations?.length ?? 0} model
                                calls
                            </Typography>
                        }
                    >
                        {exploration ? (
                            <ExplorationNarrative
                                exploration={exploration}
                                rootQuestionId={question.id}
                                totalConversations={totalConversations}
                            />
                        ) : analysis ? (
                            <AnalysisNarrative
                                analysis={analysis}
                                totalConversations={totalConversations}
                            />
                        ) : null}
                        <QuestionStepCalls
                            output={output}
                            questionId={question.id}
                            phase={["MultiLevelAnalysis", "Analysis"]}
                        />
                        {feedbackQuestionId && (
                            <StepReviewPanel
                                questionId={feedbackQuestionId}
                                scope={{ kind: "step", step: "Analysis" }}
                                label="Review this analysis"
                                placeholder="Did KBo narrow to the right cohort, and does the finding hold up?"
                            />
                        )}
                    </DigestCard>

                    <DigestCard
                        id="phase-5"
                        number={2}
                        color={pageColors.green}
                        title={`KBo writes it up for ${userName}`}
                    >
                        <Typography
                            sx={{ color: pageColors.muted, lineHeight: 1.6 }}
                        >
                            {report?.email?.previewText ??
                                "No report was produced for this question."}
                        </Typography>
                        {report?.email && reportIndex >= 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 1.5,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<EmailRounded />}
                                    onClick={() => {
                                        setPendingText("");
                                        setEmailModalOpen(true);
                                    }}
                                >
                                    Send email
                                </Button>
                            </Box>
                        )}
                        {report?.email && (
                            <ReportPreview email={report.email} />
                        )}
                        <ConfirmationDialog
                            isOpen={emailModalOpen}
                            title="Send report"
                            subTitle="Enter one or more email addresses. Leave empty to send the report to yourself."
                            maxWidth="sm"
                            fullWidth
                            onClose={() => setEmailModalOpen(false)}
                            onConfirm={handleSendEmail}
                            isLoading={emailSubmitting}
                            confirmButtonText={
                                recipients.length || pendingText.trim()
                                    ? "Send report"
                                    : "Send to me"
                            }
                            content={
                                <AcxEmailChipInput
                                    ref={emailInputRef}
                                    values={recipients}
                                    setValues={setRecipients}
                                    suggestions={emailSuggestions}
                                    loading={emailSuggestionsLoading}
                                    placeholderText="Add email addresses…"
                                    onPendingTextChange={setPendingText}
                                />
                            }
                        />
                        {report && (
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 1.25,
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(140px, 1fr))",
                                    mt: 1.5,
                                }}
                            >
                                <Stat
                                    value={report.email ? 1 : 0}
                                    label="finding delivered"
                                    accent
                                />
                                <Stat value={runClosedAt} label="run closed" />
                                <Stat
                                    value={formatDuration(
                                        output.startedAtUtc,
                                        output.completedAtUtc,
                                    )}
                                    label="wall clock"
                                />
                                <Stat
                                    value={formatNumber(totalRunCalls)}
                                    label={
                                        runFailedCalls > 0
                                            ? `model calls · ${runFailedCalls} failed`
                                            : "model calls · all succeeded"
                                    }
                                />
                            </Box>
                        )}
                        {(report?.recommendedActions ?? []).length > 0 && (
                            <Typography variant="body2" sx={{ mt: 1.5 }}>
                                <Box component="span" sx={{ fontWeight: 800 }}>
                                    Take action —
                                </Box>{" "}
                                {report?.recommendedActions?.join(" ")}
                            </Typography>
                        )}
                        <QuestionStepCalls
                            output={output}
                            questionId={question.id}
                            phase={[
                                "MultiLevelReportAssembly",
                                "ReportAssembly",
                            ]}
                        />
                        {feedbackQuestionId && (
                            <StepReviewPanel
                                questionId={feedbackQuestionId}
                                scope={{ kind: "step", step: "Report" }}
                                label="Review this report"
                                placeholder="Is this what the recipient should have received — tone, framing, and recommended actions?"
                            />
                        )}
                    </DigestCard>
                </>
            ) : (
                <DigestCard
                    number={1}
                    color={pageColors.orange}
                    title="Where the trail ends"
                    sx={{
                        bgcolor: pageColors.orangeSoft,
                        borderColor: "#FED7AA",
                    }}
                >
                    <Typography
                        sx={{ color: pageColors.muted, lineHeight: 1.6 }}
                    >
                        No cohort, analysis, or report was created for this
                        question. It was accepted on quality, but{" "}
                        <Box component="span" sx={{ fontWeight: 800 }}>
                            maxQuestionsToPursue was {maxPursue}
                        </Box>
                        , so the run continued with the higher-ranked question.
                        This is queue depth, not a negative verdict.
                    </Typography>
                </DigestCard>
            )}
        </Box>
    );
}
